import React, { useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { Tooltip } from 'antd';
import { useDispatch } from 'react-redux';
import { ReactComponent as CursorSvg } from '@assets/icons/ShapesDrawingTools/cursor.svg';
import { ReactComponent as CommentSvg } from '@assets/icons/ShapesDrawingTools/comment.svg';
import { ReactComponent as ShowCommentSvg } from '@assets/icons/ShapesDrawingTools/show_comment.svg';
import { useAuth } from '@hooks/useAuth';
import { toggleSingInExternalReviewerModal } from '@redux/actions/modalAction';
import type { IAssetViewerStateContextValue } from '@context/AssetViewerStateContext';
import { withKeysCanvasHistoryCopy } from '@helpers/withKeysCanvasHistoryChange';

import './CompareVersionTools.less';

export type CompareVersionToolsProps = {
  leftAssetViewerState: IAssetViewerStateContextValue | null;
  rightAssetViewerState: IAssetViewerStateContextValue | null;
};

function CompareVersionTools({
  leftAssetViewerState,
  rightAssetViewerState
}: CompareVersionToolsProps) {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const isHideComments =
    leftAssetViewerState?.isHideComments ??
    rightAssetViewerState?.isHideComments;
  const shapesDrawingTools =
    leftAssetViewerState?.shapesDrawingTools ??
    rightAssetViewerState?.shapesDrawingTools;
  const canvasMode =
    leftAssetViewerState?.canvasMode ?? rightAssetViewerState?.canvasMode;
  const selectedCommentId =
    leftAssetViewerState?.selectedCommentId ??
    rightAssetViewerState?.selectedCommentId;
  const isCommentFieldFocused =
    leftAssetViewerState?.isCommentFieldFocused ??
    rightAssetViewerState?.isCommentFieldFocused;
  const isShapeDrawing = shapesDrawingTools?.shape !== 'cursor';

  const undoRedoTooltips = useMemo(() => {
    return withKeysCanvasHistoryCopy();
  }, []);

  const setHideComments = useCallback(() => {
    leftAssetViewerState?.setHideComments(!isHideComments);
    rightAssetViewerState?.setHideComments(!isHideComments);
  }, [
    isHideComments,
    leftAssetViewerState?.setHideComments,
    rightAssetViewerState?.setHideComments
  ]);

  const selectComment = useCallback(
    (id: string | null) => {
      leftAssetViewerState?.selectComment(id);
      rightAssetViewerState?.selectComment(id);
    },
    [leftAssetViewerState?.selectComment, rightAssetViewerState?.selectComment]
  );

  const setDocumentHighlight = useCallback(() => {
    leftAssetViewerState?.setDocumentHighlight(null);
    rightAssetViewerState?.setDocumentHighlight(null);
  }, [
    leftAssetViewerState?.setDocumentHighlight,
    rightAssetViewerState?.setDocumentHighlight
  ]);

  const setCanvasMode = useCallback(
    (mode: 'view' | 'edit') => {
      leftAssetViewerState?.setCanvasMode(mode);
      rightAssetViewerState?.setCanvasMode(mode);
    },
    [leftAssetViewerState?.setCanvasMode, rightAssetViewerState?.setCanvasMode]
  );

  const setDrawToolsShape = useCallback(
    (shape: string) => {
      leftAssetViewerState?.setDrawToolsShape(shape);
      rightAssetViewerState?.setDrawToolsShape(shape);
    },
    [
      leftAssetViewerState?.setDrawToolsShape,
      rightAssetViewerState?.setDrawToolsShape
    ]
  );

  const onShapeClick = useCallback(
    (shape: string) => {
      if (shape === 'cursor') {
        setDrawToolsShape('cursor');
        selectComment(null);
        setCanvasMode('view');
        return;
      }
      if (!user) {
        dispatch(
          toggleSingInExternalReviewerModal({
            visible: true
          })
        );
        return;
      }
      if (canvasMode === 'view') {
        if (selectedCommentId !== 'reply') {
          selectComment('new');
          setDocumentHighlight();
        }
        setCanvasMode('edit');
      }
      setDrawToolsShape(shape);
    },
    [
      canvasMode,
      dispatch,
      selectedCommentId,
      user,
      setDrawToolsShape,
      selectComment,
      setDocumentHighlight,
      setCanvasMode
    ]
  );

  useEffect(() => {
    if (
      (rightAssetViewerState?.shapesDrawingTools?.shape === 'cursor' &&
        leftAssetViewerState?.shapesDrawingTools?.shape !== 'cursor') ||
      (leftAssetViewerState?.shapesDrawingTools?.shape !== 'cursor' &&
        rightAssetViewerState?.shapesDrawingTools?.shape === 'cursor')
    ) {
      setDrawToolsShape('cursor');
      selectComment(null);
      setCanvasMode('view');
    }
  }, [
    leftAssetViewerState?.shapesDrawingTools?.shape,
    rightAssetViewerState?.shapesDrawingTools?.shape
  ]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const fieldElement = document.querySelector(
        '.filter-sort-panel-search-input'
      );
      const transcriptSearchInput = document.getElementById(
        'TRANSCRIPT_SEARCH_INPUT'
      );

      const key = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && key === 'h') {
        e.preventDefault();
        setHideComments();
        return;
      }
      if (fieldElement || transcriptSearchInput || isCommentFieldFocused) {
        return;
      }
      if (key === 'escape' || key === 'v') {
        onShapeClick('cursor');
        return;
      }
      if (key === 'c') {
        onShapeClick('pin');
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [setHideComments, onShapeClick, isCommentFieldFocused]);

  return (
    <div
      className={classNames(
        isShapeDrawing && 'compare_version_tools_container_open',
        'compare_version_tools_container'
      )}
    >
      <div className="wrapper">
        <Tooltip
          title={
            <div className="media-viewer-tooltip-inner">
              Select
              <span className="media-viewer-tooltip-label">V</span>
            </div>
          }
          overlayInnerStyle={{ padding: 0 }}
          placement="right"
          overlayClassName="toggle_favorite_overlay"
        >
          <span
            role="button"
            tabIndex={0}
            aria-label="Cursor"
            className={classNames(
              shapesDrawingTools?.shape === 'cursor' && 'selected',
              'anticon anticon-font-size'
            )}
            onClick={() => {
              onShapeClick('cursor');
            }}
          >
            <CursorSvg />
          </span>
        </Tooltip>

        <Tooltip
          title={
            <div className="media-viewer-tooltip-inner">
              Comment
              <span className="media-viewer-tooltip-label">C</span>
            </div>
          }
          overlayInnerStyle={{ padding: 0 }}
          placement="right"
          overlayClassName="toggle_favorite_overlay"
        >
          <span
            role="button"
            tabIndex={0}
            aria-label="Add comment"
            className={classNames(
              shapesDrawingTools?.shape === 'pin' && 'selected',
              'anticon anticon-font-size'
            )}
            onClick={() => {
              onShapeClick('pin');
            }}
          >
            <CommentSvg />
          </span>
        </Tooltip>
        <span className="divider b-full" />
        <Tooltip
          title={
            <div className="media-viewer-tooltip-inner">
              {isHideComments ? 'Show' : 'Hide'} comments
              <span className="media-viewer-tooltip-label">
                {undoRedoTooltips.toggleShowComments}
              </span>
            </div>
          }
          overlayInnerStyle={{ padding: 0 }}
          placement="right"
          overlayClassName="toggle_favorite_overlay"
        >
          <span
            role="button"
            tabIndex={0}
            aria-label="Toggle comments visibility"
            onClick={setHideComments}
            className={classNames(
              isHideComments && 'selected',
              'anticon anticon-font-size shapes_drawing_tools-item'
            )}
          >
            <ShowCommentSvg />
          </span>
        </Tooltip>
      </div>
    </div>
  );
}
export default CompareVersionTools;
